import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import UploadDialog from "@/components/modals/UploadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { gbp, num, formatDate } from "@/lib/format";
import { Plus, UploadCloud, Search, Pencil, Trash2, Download } from "lucide-react";

const BLANK = { product_code: "", description: "", product_cat: "", invoiced_price: 0, delta_price: 0, total_units: 0, total_shipthru: 0 };

export default function ProductsTable() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });

  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const cats = useMemo(() => [...new Set(data.map((d) => d.product_cat).filter(Boolean))].sort(), [data]);
  const filtered = useMemo(() => data.filter((r) => {
    if (cat !== "all" && r.product_cat !== cat) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${r.product_code} ${r.description} ${r.product_cat}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [data, cat, search]);

  const openAdd = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ product_code: r.product_code, description: r.description, product_cat: r.product_cat, invoiced_price: r.invoiced_price, delta_price: r.delta_price, total_units: r.total_units, total_shipthru: r.total_shipthru });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.product_code.trim()) { toast.error("Product code is required"); return; }
    setSaving(true);
    try {
      const p = { ...form };
      ["invoiced_price", "delta_price", "total_units", "total_shipthru"].forEach((k) => (p[k] = Number(p[k]) || 0));
      if (editing) await api.put(`/products/${editing.id}`, p);
      else await api.post("/products", p);
      toast.success(editing ? "Product updated" : "Product added");
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setDialogOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success("Product deleted");
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const exportCsv = () => {
    const header = ["product_code", "description", "product_cat", "invoiced_price", "delta_price", "total_units", "total_shipthru"];
    const rows = filtered.map((r) => header.map((h) => `"${r[h] ?? ""}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "epgroup-products.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader title="Products" subtitle="Product database with codes, pricing and delta. Upload to keep values up to date.">
        <Button variant="outline" onClick={exportCsv} data-testid="product-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
        <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="product-upload-button"><UploadCloud className="w-4 h-4 mr-1.5" /> Upload</Button>
        <Button onClick={openAdd} className="bg-[#d4333b] hover:bg-[#b82a31]" data-testid="product-add-button"><Plus className="w-4 h-4 mr-1.5" /> Add product</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search code, description or category…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full sm:w-52" data-testid="product-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : (
        <TableShell>
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">Product Code</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Invoiced Price</th>
                <th className="px-4 py-3 text-right">Delta Price</th>
                <th className="px-4 py-3 text-right">Total Units</th>
                <th className="px-4 py-3 text-right">Total Shipthru</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`product-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono-num font-medium text-slate-800 dark:text-slate-200">{r.product_code}</td>
                  <td className="px-4 py-3 text-slate-600">{r.description || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.product_cat || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{gbp(r.invoiced_price)}</td>
                  <td className={`px-4 py-3 text-right font-mono-num ${r.delta_price < 0 ? "text-red-600" : "text-emerald-600"}`}>{gbp(r.delta_price)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{num(r.total_units)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{num(r.total_shipthru)}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono-num">{formatDate(r.updated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`product-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(r)} data-testid={`product-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState message="No products match your filters." />}
        </TableShell>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="product-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Product code *</Label><Input className="mt-1.5" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} data-testid="product-form-code" /></div>
            <div><Label>Category</Label><Input className="mt-1.5" value={form.product_cat} onChange={(e) => setForm({ ...form, product_cat: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Invoiced price (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.invoiced_price} onChange={(e) => setForm({ ...form, invoiced_price: e.target.value })} /></div>
            <div><Label>Delta price (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.delta_price} onChange={(e) => setForm({ ...form, delta_price: e.target.value })} /></div>
            <div><Label>Total units</Label><Input className="mt-1.5" type="number" value={form.total_units} onChange={(e) => setForm({ ...form, total_units: e.target.value })} /></div>
            <div><Label>Total shipthru</Label><Input className="mt-1.5" type="number" value={form.total_shipthru} onChange={(e) => setForm({ ...form, total_shipthru: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#d4333b] hover:bg-[#b82a31]" data-testid="product-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        entity="products"
        columns={["product_code", "description", "product_cat", "invoiced_price", "delta_price", "total_units", "total_shipthru"]}
        onDone={() => { qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["stats"] }); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove <span className="font-medium">{deleteTarget?.product_code}</span>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
